import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  CircularProgress,
  Typography,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getPnAccessTokenFromCookie } from "../../../../../../../../../lib/itn-ui-kit/data/store";
import ViewManagementLink from "../../../../../../../../components/modules/dynamic-views/view-management/components/ViewManagementLink";
import useInterval from "../../../../../../../../hooks/useInterval";
import useLiveServerEvents from "../../../../../../../../hooks/useLiveServerEvents";
import useService from "../../../../../../../../hooks/useService";
import { SSE_EVENT_TYPES } from "../../../../../../../../services/sse-service";
import ProviderDetail from "../../../../../../../modules/providers/provider-detail";
import Svg, { SvgEnum } from "../../../../../../../ui library/components/svgs/standard/svg";
import HNSColors from "../../../../../../../ui library/HNSColors";
import ReportTooltip from "../../ReportsList/components/ReportTooltip";
import { COL_CONTENT_MAX_LENGTH, COL_HEADER_MAX_LENGTH } from "../utils/consts";

import ClientType from "./columnTypes/ClientType";
import DateType from "./columnTypes/DateType";
import ProviderType from "./columnTypes/ProviderType";

const ReportTable = ({
  reportData,
  filters,
  updateFilter,
  reportId,
  tableSx = {},
  setReportTotalCount,
  externalLoading = false,
  onDataLoaded = () => {},
}) => {
  const tabularReportRepository = useService("repositories/tabular-report-repository");
  const reportDataRepository = useService("repositories/report-data-repository");
  const navigate = useNavigate();
  const ordering = filters.get("ordering") || "";
  const [isLoading, setIsLoading] = useState(false);
  const [pendingDataId, setPendingDataId] = useState(undefined);
  const [requestTime, setRequestTime] = useState(undefined);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [reportResults, setResults] = useState(reportData?.results || []);
  const handleProviderOpen = (provider) => {
    const providerId = provider?.pk || provider?.id;
    if (!providerId) {
      return;
    }
    setSelectedProvider({ pk: providerId });
  };

  const closeProviderModal = () => setSelectedProvider(null);

  const dataCellTypography = {
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "100%",
    letterSpacing: "0",
    color: `var(--report-text-color, ${HNSColors.neutrals[100]})`,
  };

  const headerTypography = {
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "100%",
    letterSpacing: "0",
    color: HNSColors.neutrals[100],
  };
  const [token, setToken] = useState(null);

  useEffect(() => {
    getPnAccessTokenFromCookie().then(setToken);
  }, []);
  async function setReportHelper(payload, eventType, rawEvent) {
    if (payload?.type === SSE_EVENT_TYPES.DATA_READY) {
      if (payload?.access_pk === pendingDataId) {
        const data = await reportDataRepository.run(pendingDataId);
        const results = data?.results || [];
        processReportData(results, data);
      }
    }
  }
  const setReportData = useCallback(
    (payload, eventType, rawEvent) => {
      setReportHelper(payload, eventType, rawEvent);
    },
    [pendingDataId, requestTime]
  );

  useLiveServerEvents({
    accessToken: token,
    manageConnection: false,
    onAnyEventReceived: setReportData,
  });
  function processReportData(results, data) {
    setResults(results);
    setReportTotalCount(data?.count || results.length);
    onDataLoaded();
    setIsLoading(false);
    setRequestTime(undefined);
    setPendingDataId(undefined);
  }

  async function pullData() {
    if (isLoading === true) {
      return;
    }
    try {
      setResults([]);
      setIsLoading(true);
      const data = await tabularReportRepository.run(reportId, filters);
      setPendingDataId(data?.dataAccessPk);
      setRequestTime(new Date());
    } catch (error) {
      console.error(error);
      onDataLoaded();
      setIsLoading(false);
      navigate("/reports/list", { replace: true });
      return error;
    }
  }
  async function checkForResults() {
    if (isLoading === false || !pendingDataId) {
      return;
    }
    try {
      const data = await reportDataRepository.findRecord(pendingDataId);
      if (!data.queryTime) {
        return;
      }
      const report_data = await reportDataRepository.run(pendingDataId);
      const results = report_data?.results || [];
      processReportData(results, report_data);
    } catch (error) {
      console.error(error);
      onDataLoaded();
      setIsLoading(false);
      navigate("/reports/list", { replace: true });
      return error;
    }
  }
  useEffect(() => {
    pullData();
  }, [reportData]);

  useInterval(
    () => {
      // Stop interval if more than 2 minutes have passed since requestTime
      if (requestTime && Date.now() - new Date(requestTime).getTime() > 3 * 60 * 1000) {
        setPendingDataId(undefined);
        setRequestTime(undefined);
        setIsLoading(false);
        onDataLoaded();
        return;
      }
      checkForResults();
    },
    pendingDataId ? 1000 : null // checking every 30000 seconds if the data
  );
  const displayLoading = isLoading || externalLoading;

  const columns = useMemo(() => {
    const cols = reportData.columns ?? [];
    if (Array.isArray(cols)) {
      return cols;
    }
    return Object.keys(cols).map((k) => cols[k]);
  }, [reportData.columns]);

  const getCellValue = (val) => {
    if (val === null || val === undefined) {
      return null;
    }
    if (typeof val === "object" && val !== null && "value" in val) {
      return val.value;
    }
    return val;
  };

  const truncateText = (text) => {
    if (text.length > COL_CONTENT_MAX_LENGTH) {
      return { text: `${text.slice(0, COL_CONTENT_MAX_LENGTH)}...`, isTruncated: true };
    }
    return { text, isTruncated: false };
  };

  const getHeaderDisplayText = (text) => {
    if (text.length > COL_HEADER_MAX_LENGTH) {
      return { normalized: text, display: `${text.slice(0, COL_HEADER_MAX_LENGTH)}...` };
    }
    return { normalized: text, display: text };
  };

  const renderPolarityValue = (displayValue, polarity) => {
    const polarityStyles = {
      positive: {
        backgroundColor: HNSColors.success[800],
        color: HNSColors.success[400],
      },
      negative: {
        backgroundColor: HNSColors.failure[800],
        color: HNSColors.failure[400],
      },
      neutral: {},
    };

    const styling = polarityStyles[polarity] ?? {};

    if (polarity === "neutral") {
      return (
        <span style={dataCellTypography} data-polarity="false">
          {displayValue}
        </span>
      );
    }

    return (
      <Box
        component="span"
        sx={{
          ...dataCellTypography,
          ...styling,
          borderRadius: "999px",
          px: 1,
          py: 0.5,
          display: "inline-flex",
          alignItems: "center",
          width: "100%",
        }}
        data-polarity="true"
      >
        {displayValue}
      </Box>
    );
  };

  const renderColorStateValue = (displayValue, colorState) => {
    const stateStyles = {
      green: {
        backgroundColor: HNSColors.success[800],
        color: HNSColors.success[400],
      },
      yellow: {
        backgroundColor: HNSColors.warning[600],
        color: HNSColors.warning[800],
      },
      red: {
        backgroundColor: HNSColors.failure[800],
        color: HNSColors.failure[400],
      },
    };
    const styling = stateStyles[colorState] ?? {};

    return (
      <Box
        component="span"
        sx={{
          ...dataCellTypography,
          ...styling,
          borderRadius: "999px",
          px: 1,
          py: 0.5,
          display: "inline-flex",
          alignItems: "center",
          width: "100%",
        }}
        data-color-state={colorState}
      >
        {displayValue}
      </Box>
    );
  };

  const renderFormattedValue = (rawValue, columnMeta = {}) => {
    const isCurrencyColumn = columnMeta.type === "currency";
    const value = getCellValue(rawValue);

    if (
      value === null ||
      value === undefined ||
      (typeof value === "string" && value.trim() === "")
    ) {
      return (
        <span style={dataCellTypography} data-polarity="false">
          NO Value
        </span>
      );
    }

    if (isCurrencyColumn) {
      const polarity = columnMeta.polarity;
      return renderPolarityValue(value, polarity);
    }

    if (rawValue && typeof rawValue === "object" && rawValue.color_state) {
      return renderColorStateValue(value, rawValue.color_state);
    }

    if (typeof value === "boolean") {
      return (
        <span style={dataCellTypography} data-polarity="false">
          {value ? "True" : "False"}
        </span>
      );
    }

    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (normalized === "true" || normalized === "false") {
        return (
          <span style={dataCellTypography} data-polarity="false">
            {normalized === "true" ? "True" : "False"}
          </span>
        );
      }
    }

    const fullText = String(value);
    const { text: safeText, isTruncated } = truncateText(fullText);
    return (
      <ReportTooltip
        description={isTruncated ? fullText : ""}
        arrowPlacement="top"
        allowHover={isTruncated}
        sxContainer={{
          justifyContent: "start",
        }}
        customContent={
          <span style={dataCellTypography} data-polarity="false">
            {safeText}
          </span>
        }
      />
    );
  };

  const formatColumns = (column, cellData) => {
    const columnType = column?.type;

    switch (columnType) {
      case "ClientColumn":
        return <ClientType clientObj={cellData} />;
      case "DateTimeColumn":
        return <DateType date={cellData?.value} newLine />;
      case "ProviderColumn":
        return (
          <ProviderType
            providerId={cellData?.id}
            providerName={cellData?.value}
            onOpenProvider={handleProviderOpen}
          />
        );
      case "LinkColumn":
        return (
          <ViewManagementLink viewName={cellData?.target} id={cellData?.id}>
            {cellData?.value}
          </ViewManagementLink>
        );
      default:
        return renderFormattedValue(cellData, column);
    }
  };

  const createSortHandler = (property) => () => {
    updateFilter(
      "ordering",
      ordering === property ? `-${property}` : ordering === `-${property}` ? "" : property
    );
  };

  const TableHeader = () => (
    <TableHead>
      <TableRow>
        {columns.map((col) => {
          const isOrderingActive = ordering === col.field_name || ordering === `-${col.field_name}`;
          const arrowDirection = ordering.startsWith("-") ? "desc" : "asc";
          const { display: headerDisplay } = getHeaderDisplayText(String(col.display ?? ""));

          const SortIcon = (iconProps) => (
            <Svg
              {...iconProps}
              name={
                iconProps.className.includes("MuiTableSortLabel-iconDirectionAsc")
                  ? SvgEnum.HisArrowUp
                  : SvgEnum.HisArrowDown
              }
              color={isOrderingActive ? HNSColors.primary[500] : HNSColors.neutrals[500]}
              sx={{ width: 10, height: 10, margin: "0 4px" }}
            />
          );

          return (
            <TableCell
              key={col.field_name}
              sx={{
                ...headerTypography,
                borderBottom: `2px solid ${HNSColors.neutrals[800]}`,
                backgroundColor: HNSColors.neutrals[900],
                minWidth: "fit-content",
              }}
              sortDirection={isOrderingActive ? arrowDirection : false}
            >
              <TableSortLabel
                className="line-clamp-1"
                active={isOrderingActive}
                direction={isOrderingActive ? arrowDirection : "asc"}
                onClick={createSortHandler(col.field_name)}
                sx={{
                  ...headerTypography,
                  minWidth: "max-content",
                  color: headerTypography.color,
                  "&.Mui-active": {
                    color: HNSColors.primary[500],
                  },
                  "& .MuiTableSortLabel-icon": {
                    opacity: 1,
                    color: isOrderingActive
                      ? `${HNSColors.primary[500]} !important`
                      : `${HNSColors.neutrals[500]} !important`,
                  },
                }}
                IconComponent={SortIcon}
              >
                {headerDisplay}
                {isOrderingActive ? (
                  <Box component="span" sx={visuallyHidden}>
                    {arrowDirection === "desc" ? "sorted descending" : "sorted ascending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );

  const createRowCells = (rowIndex) =>
    columns.map((cell) => {
      const fieldName = cell.field_name;
      return (
        <TableCell
          key={fieldName + "_" + rowIndex}
          sx={{
            borderBottom: `1px solid ${HNSColors.neutrals[800]}`,
            ...dataCellTypography,
            color: HNSColors.neutrals[100],
            py: 1.5,
          }}
        >
          {formatColumns(cell, reportResults[rowIndex][fieldName])}
        </TableCell>
      );
    });

  return (
    <div
      id="Report-Table"
      data-id="Report-Table"
      className="flex flex-1 flex-col"
      style={{ height: "calc(100vh - 300px)", ...tableSx }}
    >
      <TableContainer
        className="flex-1 overflow-auto"
        sx={{
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: HNSColors.primary[500],
            borderRadius: "999px",
          },
        }}
      >
        <Table stickyHeader size="small">
          <TableHeader />

          {displayLoading && (
            <TableBody>
              <TableRow>
                <TableCell colSpan={Math.max(columns.length, 1)}>
                  <Box display="flex" alignItems="center" justifyContent="center" py={4}>
                    <CircularProgress
                      sx={{
                        color: HNSColors.primary[500],
                      }}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            </TableBody>
          )}

          {!displayLoading && !reportResults && (
            <TableBody>
              <TableRow>
                <TableCell colSpan={Math.max(columns.length, 1)}>
                  <Box display="flex" alignItems="center" justifyContent="center" py={4}>
                    <Typography variant="body2" color="text.secondary">
                      No results found.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            </TableBody>
          )}

          {!displayLoading && reportResults && (
            <TableBody>
              {reportResults.map((_row, idx) => (
                <TableRow
                  key={"rows_" + idx}
                  sx={{
                    "--report-link-color": HNSColors.primary[500],
                    "--report-text-color": HNSColors.neutrals[100],
                    backgroundColor:
                      idx % 2 === 0 ? HNSColors.tertiary[950] : HNSColors.neutrals[900],
                    transition: "background-color 150ms ease, color 150ms ease",
                    "&:hover": {
                      backgroundColor: HNSColors.primary[500],
                      "--report-link-color": HNSColors.neutrals[900],
                      "--report-text-color": HNSColors.neutrals[900],
                      color: HNSColors.neutrals[900],
                      "& td": {
                        color: HNSColors.neutrals[900],
                      },
                      "& td span[data-polarity='false']": {
                        color: HNSColors.neutrals[900],
                      },
                      "& a": {
                        color: `${HNSColors.neutrals[900]} !important`,
                      },
                    },
                  }}
                >
                  {createRowCells(idx)}
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      {selectedProvider && (
        <ProviderDetail providerDetail={selectedProvider} open setOpen={closeProviderModal} />
      )}
    </div>
  );
};

export default ReportTable;
